import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  X,
  CreditCard,
  Target,
  BarChart3,
  Clock,
  Building2,
  Settings,
  User,
} from "lucide-react";
import { cn } from "@shared/lib/utils";
import { useAuth } from "@shared/lib/auth";
import { Logo } from "@shared/components/Logo";

const NAV_LINKS = [
  { to: "/?panel=billing&tab=invoices", label: "Billing", icon: CreditCard },
  { to: "/my-center/leads", label: "Leads", icon: Target },
  { to: "/?panel=reports&tab=overview", label: "Reports", icon: BarChart3 },
  { to: "/history", label: "History", icon: Clock },
  { to: "/my-center", label: "My Center", icon: Building2 },
  { to: "/settings", label: "Settings", icon: Settings, adminOnly: true },
  { to: "/me", label: "My Profile", icon: User },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function AdminMoreDrawer({ isOpen, onClose }: Props) {
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const auth = useAuth();
  // Close on route change
  useEffect(() => {
    onClose();
  }, [location.pathname, location.search]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-300",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={menuRef}
        className={cn(
          "fixed right-0 top-0 z-50 h-full w-72 bg-surface shadow-xl transition-transform duration-300 ease-out",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/30 px-5 py-4">
          <Link to="/" onClick={onClose}>
            <Logo size="sm" showDots={false} />
          </Link>
          <button
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-muted hover:bg-surface2 hover:text-foreground"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Links */}
        <nav className="flex flex-col gap-2 p-4 overflow-y-auto" style={{ maxHeight: "calc(100dvh - 240px)" }}>
          <span className="mb-1 px-3 text-xs font-medium uppercase tracking-wider text-muted">
            Navigation
          </span>
          {NAV_LINKS.map((link) => {
            if (link.adminOnly && auth.user?.role !== "admin" && !auth.permissions?.can_manage_settings) return null;
            const Icon = link.icon;
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={onClose}
                className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-surface2"
              >
                <Icon className="h-4 w-4 text-muted" />
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-border/30 p-4 bg-surface">
          <div className="mb-3 px-3">
            <p className="text-sm font-medium text-foreground">
              {auth.user?.full_name || "User"}
            </p>
            <p className="text-xs text-muted">{auth.user?.email}</p>
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={() => { onClose(); void auth.signOut(); }}
              className="mt-1 rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-surface2"
            >
              Sign out
            </button>
            <button
              onClick={() => { onClose(); void auth.signOutAndForgetDevice(); }}
              className="rounded-lg px-3 py-2 text-left text-sm text-muted transition-colors hover:bg-surface2"
            >
              Sign out & forget device
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
